/**
 * Datagrid component displays the datagrid with all informations.
 * This component handles the first user inputs for different actions.
 */

import React, { useMemo, useCallback } from "react";
import DataGrid, {
  Column,
  Grouping,
  Export,
  GroupPanel,
  FilterRow,
  SearchPanel,
  Scrolling,
  Editing,
  LoadPanel,
} from "devextreme-react/data-grid";
import ProgressBarCell from "../../../../common/pyramid/ProgressBarCell";
import { osirisApi } from "../../../../../services/api/osiris";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import saveAs from "file-saver";
import { formatUTCtoLocal } from "components/common/pyramid/FormatUTCtoLocal";

/**
 * Props:
 * - companyList: list of companies
 * - userList: list of users
 * - computationList: list of computations
 * - datasetList: list of datasets
 * - onAddButtonClick: function to handle the add button click
 * - onRowClick: function to handle the row click
 * - isLoading: boolean indicating if data is loading
 */
const Datagrid = ({
  companyList,
  userList,
  computationList,
  datasetList,
  onAddButtonClick,
  onRowClick,
  isLoading,
}) => {
  const osiris = osirisApi();

  const allowDeletion = useCallback(
    (e) => {
      return e.row.data.groupId === companyList[0]?.id;
    },
    [companyList]
  );

  const onExporting = useCallback((e) => {
    e.component.beginUpdate();
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Computations");

    exportDataGrid({
      component: e.component,
      worksheet: worksheet,
      autoFilterEnabled: true,
    })
      .then(function () {
        workbook.xlsx.writeBuffer().then(function (buffer) {
          saveAs(
            new Blob([buffer], { type: "application/octet-stream" }),
            "Computations.xlsx"
          );
        });
      })
      .then(function () {
        e.component.endUpdate();
      });
  }, []);

  // Saving the toolbar items by memoization
  const toolbarItems = useMemo(
    () => [
      {
        widget: "dxButton",
        options: {
          icon: "refresh",
          onClick: () => {
            window.dispatchEvent(new CustomEvent("refreshComputations"));
          },
        },
        location: "after",
      },
      {
        widget: "dxButton",
        options: {
          icon: "add",
          onClick: onAddButtonClick,
        },
        location: "after",
      },
    ],
    [onAddButtonClick]
  );

  const onToolbarPreparing = (e) => {
    e.toolbarOptions.items.unshift(...toolbarItems);
  };

  const deleteComputation = useCallback(
    async (e) => {
      try {
        await osiris.deleteComputation(e.key);
        window.dispatchEvent(new CustomEvent("refreshComputations"));
      } catch (error) {
        console.error("Error deleting computation:", error);
      }
    },
    [osiris]
  );

  // Mémoisation des configurations des colonnes
  const columns = useMemo(
    () => [
      {
        dataField: "groupId",
        caption: "Created by company",
        groupIndex: 0,
        lookup: {
          dataSource: companyList,
          valueExpr: "id",
          displayExpr: "displayName",
        },
        groupCellTemplate: (element, data) => element.append(data.text),
      },
      {
        dataField: "userId",
        caption: "Created by user",
        groupIndex: 1,
        lookup: {
          dataSource: userList,
          valueExpr: "id",
          displayExpr: "displayName",
        },
        groupCellTemplate: (element, data) => element.append(data.text),
      },
      {
        dataField: "description",
        caption: "Description",
      },
      {
        dataField: "createdAt",
        caption: "Creation Date",
        dataType: "date",
        cellRender: (cellData) => {
          return formatUTCtoLocal(cellData.value);
        },
        calculateCellValue: (data) => {
          if (!data.createdAt) return null;
          const localDate = formatUTCtoLocal(data.createdAt);
          return typeof localDate === "string"
            ? new Date(localDate)
            : localDate;
        },
      },
      {
        dataField: "datasetId",
        name: "evaluationDate",
        caption: "Evaluation Date",
        dataType: "date",
        calculateCellValue: (data) => {
          if (!data.datasetId) return null;
          const dataset = datasetList.find((ds) => ds.id === data.datasetId);
          if (!dataset?.evaluationDate) return null;
          const localDate = formatUTCtoLocal(dataset.evaluationDate);
          return typeof localDate === "string"
            ? new Date(localDate)
            : localDate;
        },
        cellRender: (cellData) => {
          if (!cellData.value) return "";
          const formatted = formatUTCtoLocal(cellData.value);
          return formatted.split(" ")[0];
        },
      },
      {
        dataField: "datasetId",
        name: "datasetDescription",
        caption: "Dataset used",
        lookup: {
          dataSource: datasetList,
          valueExpr: "id",
          displayExpr: "description",
        },
      },
      {
        dataField: "step",
        caption: "Step",
        alignment: "center",
        cellRender: (cellData) => (
          <ProgressBarCell
            value={cellData.value}
            errorMessage={cellData.data.errorMessage}
          />
        ),
      },
      // {
      //   type: "buttons",
      //   width: 30,
      //   alignment: "center",
      //   caption: "",
      //   buttons: [
      //     {
      //       icon: "trash",
      //       onClick: deleteComputation,
      //       cssClass: "delete-button",
      //     },
      //   ],
      // },
    ],
    [companyList, userList, datasetList, deleteComputation]
  );

  // Memoization of the grid options
  const gridOptions = useMemo(
    () => ({
      dataSource: computationList,
      keyExpr: "id",
      rowAlternationEnabled: false,
      showBorders: false,
      showColumnLines: false,
      hoverStateEnabled: true,
      allowColumnResizing: true,
      width: "100%",
      height: "60vh",
    }),
    [computationList]
  );

  return (
    <div style={{ width: "100%" }}>
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      <DataGrid
        {...gridOptions}
        onToolbarPreparing={onToolbarPreparing}
        onExporting={onExporting}
        onRowRemoving={deleteComputation}
        onCellPrepared={(e) => {
          if (e.rowType === "data") {
            e.cellElement.style.setProperty("cursor", "pointer", "important");
          }
        }}
        onRowClick={(e) => e.rowType === "data" && onRowClick(e.data)}
      >
        {/* Additional parameters */}
        <FilterRow visible={true} applyFilter="auto" />
        <Scrolling mode="virtual" />
        <GroupPanel visible={true} allowColumnDragging={false} />
        <SearchPanel visible={true} highlightCaseSensitive={true} />
        <Grouping autoExpandAll={true} />
        <Export enabled={true} />
        <Editing
          mode="row"
          allowDeleting={allowDeletion}
          texts={{
            deleteRow: "",
          }}
          useIcons={true}
        />
        <LoadPanel
          indicatorSrc="/assets/img/bootstrap-spinner.svg"
          enabled={false}
        />

        {columns.map((column, index) => (
          <Column
            key={column.name || `${column.dataField}-${index}`}
            {...column}
          />
        ))}
      </DataGrid>
    </div>
  );
};

export default React.memo(Datagrid);
